import {trimEnd, truncate} from 'lodash';
import React from 'react';
import styled, {css} from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {PaletteColorsEnum} from '../../../../../libs/shared/core/src/models/api/paletteModel';
import {TeammateModel} from '../../../../../libs/shared/core/src/models/api/teammateModel';
import {HighlightModel} from '../../../../models/api/conversationHighlightModel';
import {VisualConversationSenderModel} from '../../../../models/api/conversationSendersModel';
import {
  Highlight,
  StyledHighlightStrong,
  truncateHighlightRanges,
} from '../../../inboxes/cells/header/search/highlight/highlight';

export interface SenderStyleProps {
  $hasHighlight: boolean;
  $isSelected: boolean;
}

export const StyledSenderBase = styled.span<SenderStyleProps>`
  /** If we have a highlight, rely on <Highlight /> to use the correct colors. */
  color: ${(p) => (p.$isSelected && !p.$hasHighlight ? p.theme.invertedText : p.theme.greys.shade90)};
  min-width: 0;
`;

export const StyledLeftPaddedSpan = styled(StyledSenderBase)<SenderStyleProps>`
  padding-left: 3px;
`;

export interface SenderFormattingOptions {
  useLongName?: boolean;
  showFirstCharOfLastName?: boolean;
}

export function formatSenderName(
  sender: VisualConversationSenderModel,
  highlight: HighlightModel | undefined,
  isCellSelected: boolean | undefined,
  options?: SenderFormattingOptions,
) {
  if (sender.isMe) {
    const me = <Trans id="sender-me">Me</Trans>;

    if (!highlight) {
      return me;
    }

    // If our own sender should be highlighted, highlight all of 'Me'.
    return <StyledHighlightStrong $backgroundColor={PaletteColorsEnum.YELLOW}>{me}</StyledHighlightStrong>;
  }

  const firstCharOfLastNameSuffix = sender.lastName ? ` ${sender.lastName.charAt(0)}.` : '';
  const fullName = `${sender.firstName} ${sender.lastName}`.trim();
  const maybeFirstCharOfLastNameSuffix = options?.showFirstCharOfLastName ? firstCharOfLastNameSuffix : '';

  if (highlight) {
    const shortName = sender.firstName;

    // If the text from the highlight corresponds to the sender name,
    // try to be smart and display the first name and the first char of the last name.
    if (!options?.useLongName && shortName && fullName === highlight.text) {
      // Try to highlight the first name only if possible.
      const isOnlyFirstNameHighlight = highlight.ranges.every(
        (range) => range.start + range.length <= shortName.length,
      );

      // (Else we fallback to the default full-name highlight below.)
      if (isOnlyFirstNameHighlight) {
        const text = shortName + maybeFirstCharOfLastNameSuffix;

        return (
          <Highlight
            text={text}
            ranges={truncateHighlightRanges(highlight.ranges, text.length)}
            backgroundColor={PaletteColorsEnum.YELLOW}
            isParentSelected={isCellSelected}
          />
        );
      }
    }

    // Otherwise, just show the given highlight.
    return (
      <Highlight
        text={highlight.text}
        ranges={highlight.ranges}
        backgroundColor={PaletteColorsEnum.YELLOW}
        isParentSelected={isCellSelected}
      />
    );
  }

  let name;
  if (sender.firstName && !options?.useLongName) {
    // When using the first name only, trim some punctuation characters.
    // Some recipients have their name written like "LastName, FirstName" others have "Dr. FirstName LastName".
    const trimmedName = truncateFirstNameForSenderLine(trimEnd(sender.firstName, ' .,'));

    name = trimmedName + maybeFirstCharOfLastNameSuffix;
  } else {
    name = fullName;
  }

  if (!name) {
    return (
      <em>
        <Trans id="sender-unnamed">Unnamed</Trans>
      </em>
    );
  }

  return <>{name}</>;
}

export function makeEllipsisElement(isCellSelected: boolean, isSearchMatchingOnOtherSenders: boolean) {
  const maybeHighlightedEllipsis = isSearchMatchingOnOtherSenders ? (
    <StyledHighlightStrong $backgroundColor={PaletteColorsEnum.YELLOW}>…</StyledHighlightStrong>
  ) : (
    '…'
  );

  return (
    <StyledLeftPaddedSpan
      key="ellipsis"
      $hasHighlight={isSearchMatchingOnOtherSenders}
      $isSelected={isCellSelected}
    >
      {maybeHighlightedEllipsis}
    </StyledLeftPaddedSpan>
  );
}

export function computeOtherParticipantsCount(
  isUserTracking: boolean,
  trackerTeammates: ReadonlyArray<TeammateModel>,
  senderOne: VisualConversationSenderModel | undefined,
  senderTwo: VisualConversationSenderModel | undefined,
) {
  // It is possible for senders to have been removed from a discussion and
  // not be participants anymore.
  const senderParticipants = trackerTeammates.filter(
    (teammate) => teammate.id === senderOne?.teammateId || teammate.id === senderTwo?.teammateId,
  );

  // trackerTeammates does not include the user's tracker if it exists.
  // We need to count the user if it's not one of the displayed senders.
  const shouldCountSelf = isUserTracking && !senderOne?.isMe && !senderTwo?.isMe;

  return trackerTeammates.length - senderParticipants.length + Number(shouldCountSelf);
}

// senderOne and senderTwo are any senders that need to be excluded from the trackers.
// They are not the first and second senders.
export function makeOtherParticipantsCount(
  otherParticipantsCount: number,
  isSearchMatchingOnOtherSenders: boolean,
  isCellSelected: boolean,
) {
  if (otherParticipantsCount === 0) {
    return null;
  }

  const countText = `+${otherParticipantsCount}`;

  const countNode = isSearchMatchingOnOtherSenders ? (
    <StyledHighlightStrong $backgroundColor={PaletteColorsEnum.YELLOW}>{countText}</StyledHighlightStrong>
  ) : (
    countText
  );

  return [
    <StyledSenderBase key="others-comma" $hasHighlight={false} $isSelected={isCellSelected}>
      ,
    </StyledSenderBase>,
    <StyledLeftPaddedSpan
      key="others"
      $hasHighlight={isSearchMatchingOnOtherSenders}
      $isSelected={isCellSelected}
    >
      {countNode}
    </StyledLeftPaddedSpan>,
  ];
}

/*
 * Helpers.
 */

export function shrinkFlexItemFirst() {
  return css`
    flex-shrink: 0;
    flex-grow: 1;
    flex-basis: 0;
  `;
}

export function shrinkFlexItemLast() {
  return css`
    flex-shrink: 1;
    flex-grow: 0;
  `;
}

// Maximimum number of characters to show for first names. Includes the omission character.
const senderLineFirstNameTruncationLimit = 11;
const senderLineFirstNameTruncationOmission = '.';

export function truncateFirstNameForSenderLine(firstName: string) {
  return truncate(firstName, {
    length: senderLineFirstNameTruncationLimit,
    omission: senderLineFirstNameTruncationOmission,
  });
}
